import React, { useMemo } from 'react';
import {
  pathAChallenges,
  pathBChallenges,
  pathCChallenges,
  pathDChallenges,
  ChallengeOption
} from '@/data/challengeOptions';
import { microSessionCatalog } from '@/data/microSessions';
import { Link } from 'react-router-dom';

type FocusGroup = {
  id: string;
  label: string;
  description: string;
  challenges: Record<string, ChallengeOption>;
};

const focusGroups: FocusGroup[] = [
  {
    id: 'Communication/Fundraising',
    label: 'Fundraising & Comms',
    description: 'Voice templates, repurposing, and quality guardrails.',
    challenges: pathAChallenges
  },
  {
    id: 'Programs/Projects',
    label: 'Programs & Projects',
    description: 'Decision support, plans, monitoring, and workload balance.',
    challenges: pathBChallenges
  },
  {
    id: 'Efficiency/Systems',
    label: 'Efficiency & Systems',
    description: 'Systems, automation, and documentation speed.',
    challenges: pathCChallenges
  },
  {
    id: 'Assessment',
    label: 'Assessment (Not sure yet)',
    description: 'Diagnostic route when the user asks for guidance.',
    challenges: pathDChallenges
  }
];

const formatLabels: Record<string, string> = {
  guided_chat: 'Chat',
  scenario: 'Scenario',
  tool_lab: 'Lab',
  pulse: 'Pulse',
  reflection: 'Reflect'
};

const PathMapPage: React.FC = () => {
  const lessonLookup = useMemo(() => {
    return microSessionCatalog.reduce((acc, lesson) => {
      acc[lesson.id] = lesson;
      return acc;
    }, {} as Record<string, (typeof microSessionCatalog)[number]>);
  }, []);

  const catalogList = useMemo(() => {
    return [...microSessionCatalog].sort((a, b) => {
      if (a.skillArea === b.skillArea) return a.title.localeCompare(b.title);
      return a.skillArea.localeCompare(b.skillArea);
    });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-6xl mx-auto px-4 py-10 space-y-8">
        <header className="space-y-3">
          <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Onboarding → Path map</p>
          <h1 className="text-3xl font-semibold tracking-tight">How choices map to micro lessons</h1>
          <p className="text-slate-600 max-w-3xl">
            Role is currently informational. Focus → Challenge picks a predefined path. Style tweaks ordering:
            selecting <strong>Quick Win</strong> moves the quick win lesson to the front; <strong>Exploration</strong> keeps the same lessons but switches the phase to daily browsing.
          </p>
          <div className="inline-flex flex-wrap gap-2 text-xs text-slate-500 bg-white border border-slate-200 rounded-full px-4 py-2 shadow-sm">
            <span className="font-semibold text-slate-700">Legend</span>
            <span>• Lab = hands-on builder</span>
            <span>• Chat = guided chat</span>
            <span>• Scenario = branching scenario</span>
          </div>
        </header>

        <section className="grid gap-6">
          {focusGroups.map((group) => (
            <div key={group.id} className="rounded-3xl border border-slate-200 bg-white shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Focus</p>
                  <h2 className="text-xl font-semibold">{group.label}</h2>
                  <p className="text-sm text-slate-600">{group.description}</p>
                </div>
                <span className="text-xs text-slate-500">{Object.keys(group.challenges).length} challenge routes</span>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {Object.values(group.challenges).map((challenge) => {
                  const quickWin = lessonLookup[challenge.quickWinId];
                  return (
                    <div
                      key={challenge.id}
                      className="rounded-2xl border border-slate-100 bg-slate-50 p-4 space-y-3 shadow-inner"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Challenge {challenge.id.toUpperCase()}</p>
                          <h3 className="text-lg font-semibold text-slate-900">{challenge.title}</h3>
                          <p className="text-sm text-slate-600">{challenge.problem}</p>
                        </div>
                        {quickWin && (
                          <div className="rounded-full bg-emerald-50 text-emerald-700 text-[11px] uppercase tracking-[0.2em] px-3 py-1 border border-emerald-100">
                            Quick win: {quickWin.title}
                          </div>
                        )}
                      </div>

                      <div className="rounded-xl border border-white bg-white p-3 space-y-2 shadow-sm">
                        <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Structured path</p>
                        <ol className="space-y-2 text-sm text-slate-800">
                          {challenge.learningPath.map((step, idx) => {
                            const lesson = lessonLookup[step.id];
                            return (
                              <li key={step.id} className="flex items-start gap-2">
                                <span className="mt-[3px] h-5 w-5 rounded-full bg-slate-900 text-white text-[10px] flex items-center justify-center">
                                  {idx + 1}
                                </span>
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-semibold">{lesson?.title || step.id}</span>
                                    {lesson?.skillArea && (
                                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                        {lesson.skillArea}
                                      </span>
                                    )}
                                    {lesson?.format && (
                                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                                        {formatLabels[lesson.format] || lesson.format}
                                      </span>
                                    )}
                                    {lesson?.estimatedMinutes && (
                                      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                                        {lesson.estimatedMinutes}m
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-slate-600 text-sm">{step.why}</p>
                                  {lesson?.route && (
                                    <Link to={lesson.route} className="text-[12px] text-slate-700 underline underline-offset-4 hover:text-slate-900">
                                      View lesson
                                    </Link>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ol>
                      </div>

                      <p className="text-xs text-slate-500">
                        Style picker behavior: Quick Win surfaces <strong>{quickWin?.title || challenge.quickWinId}</strong> first; Exploration uses the same list but shifts the experience to a daily phase.
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        <section className="rounded-3xl border border-slate-200 bg-white shadow-sm p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500">Full catalog</p>
              <h2 className="text-xl font-semibold">All micro lessons</h2>
              <p className="text-sm text-slate-600">Everything currently available in the hub, sorted by skill area.</p>
            </div>
            <span className="text-xs text-slate-500">{catalogList.length} lessons</span>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {catalogList.map((lesson) => (
              <div
                key={lesson.id}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-4 flex flex-col gap-2 shadow-inner"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                      {lesson.skillArea}
                    </p>
                    <h3 className="text-base font-semibold text-slate-900">{lesson.title}</h3>
                    <p className="text-sm text-slate-600">{lesson.subTitle}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="bg-slate-100 px-2 py-0.5 rounded-full">
                      {formatLabels[lesson.format] || lesson.format}
                    </span>
                    <span>{lesson.estimatedMinutes}m</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  <span className="font-mono text-[11px] text-slate-500">{lesson.id}</span>
                  {lesson.route && (
                    <Link
                      to={lesson.route}
                      className="text-[12px] text-slate-700 underline underline-offset-4 hover:text-slate-900"
                    >
                      View route
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PathMapPage;
